import { useEffect, useRef, useState } from 'react';
import type { CancelTokenSource } from 'axios';
import axios from 'axios';

import type {
  CoveredObjectsResponse,
  CoveredObjectsResponseCoveredObjectsItem as CoveredObjectRow,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import { COVERED_OBJECTS_PAGE_SIZE } from './constants';

type UseCoveredObjectsQueryArgs = {
  organizationId: number;
  claimId: number;
  type: string;
  search: string;
  enabled?: boolean;
  // Bump from the parent (per-type counter) to force a re-fetch after a
  // mutation. Including this in the effect's dep array is the entire wiring;
  // the parent owns the trigger, the hook just observes the prop.
  refreshToken?: number;
};

type UseCoveredObjectsQueryResult = {
  items: CoveredObjectRow[];
  totalCount: number;
  // The search value that produced the current items/totalCount. Lets consumers
  // tell whether the totals they're looking at are filtered or unfiltered
  // without inferring it from the *incoming* search prop (which races ahead of
  // the in-flight fetch).
  appliedSearch: string;
  page: number;
  perPage: number;
  isLoading: boolean;
  isError: boolean;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
};

export const useCoveredObjectsQuery = ({
  organizationId,
  claimId,
  type,
  search,
  enabled = true,
  refreshToken = 0,
}: UseCoveredObjectsQueryArgs): UseCoveredObjectsQueryResult => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(COVERED_OBJECTS_PAGE_SIZE);
  const [items, setItems] = useState<CoveredObjectRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedSearch, setAppliedSearch] = useState('');
  // Initialize true so the parent's "all types ready" gate sees a "loading"
  // state on the first render — before the fetch effect runs and sets it
  // explicitly. Otherwise the initial-mount `0` from totalCount could be
  // reported as "loaded with 0" and race the threshold decision.
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  // Internal counter that drives the fetch effect (rather than wiring
  // refreshToken directly into the fetch deps). Bumping this together with
  // setPage(1) in a single render-batched effect avoids a wasted intermediate
  // GET that would otherwise fire with the stale page before page snaps to 1.
  const [refreshKey, setRefreshKey] = useState(0);

  // When the parent bumps refreshToken (after a create/edit/delete) snap
  // back to page 1 AND bump refreshKey atomically so the user lands on the
  // page that's guaranteed to show the mutated row(s).
  const isFirstRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRefresh.current) {
      isFirstRefresh.current = false;
      return;
    }
    setPage(1);
    setRefreshKey((k) => k + 1);
  }, [refreshToken]);

  // Track the latest fetch so out-of-order responses can't overwrite newer state.
  const requestSeqRef = useRef(0);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  // Filter-change snap-to-page-1 lives inside the fetch effect (not a
  // separate setPage(1) effect) so the page reset happens BEFORE the GET
  // fires — otherwise the fetch effect runs once with the new filters and
  // the stale page, wasting a request and briefly showing the wrong slice.
  const filtersKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const filtersKey = `${search}|${type}|${perPage}`;
    if (filtersKeyRef.current !== null && filtersKeyRef.current !== filtersKey && page !== 1) {
      // Filters just changed while we're past page 1 — snap to page 1; the
      // resulting render fires the fetch with the new filters + page 1.
      filtersKeyRef.current = filtersKey;
      setPage(1);
      return undefined;
    }
    filtersKeyRef.current = filtersKey;
    cancelTokenRef.current?.cancel('superseded');
    const source = axios.CancelToken.source();
    cancelTokenRef.current = source;
    const mySeq = ++requestSeqRef.current;

    // Clear prior rows so the table can't briefly show page-N rows while the
    // pagination UI already reads page-N+1. SortableTable shows its own
    // loading state when items are empty + isLoading is true.
    setItems([]);
    setIsLoading(true);
    setIsError(false);
    axios
      .get<CoveredObjectsResponse>(`/api/v1/organizations/${organizationId}/claims/${claimId}/policy/covered_objects`, {
        params: {
          type,
          q: search || undefined,
          page,
          per_page: perPage,
        },
        cancelToken: source.token,
      })
      .then((response) => {
        if (mySeq !== requestSeqRef.current) {
          return;
        }
        setItems(response.data.items);
        setTotalCount(response.data.total_count);
        setAppliedSearch(search);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        if (mySeq !== requestSeqRef.current) {
          return;
        }
        // Clear totalCount alongside the error flag so the header
        // (`Vehicles (25)`) can't keep advertising a count from a prior
        // successful fetch while the body renders the load-failure card.
        setTotalCount(0);
        setIsError(true);
        reportAxiosError(error);
      })
      .finally(() => {
        if (mySeq === requestSeqRef.current) {
          setIsLoading(false);
        }
      });

    return () => {
      source.cancel('effect cleanup');
    };
  }, [organizationId, claimId, type, search, page, perPage, enabled, refreshKey]);

  return {
    items,
    totalCount,
    appliedSearch,
    page,
    perPage,
    isLoading,
    isError,
    setPage,
    setPerPage,
  };
};
